import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, FileText } from "@phosphor-icons/react";

/**
 * Create a fresh sales invoice (manual, no upload needed).
 * Handles: series picker, bill-to/ship-to, line items with auto GST split,
 * and post-and-open-PDF flow.
 */
export default function CreateInvoiceDialog({ open, onClose, onDone }) {
    const [series, setSeries] = useState([]);
    const [seriesId, setSeriesId] = useState("");
    const [states, setStates] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState("");
    const [companyState, setCompanyState] = useState(null);
    const [saving, setSaving] = useState(false);

    const todayIso = new Date().toISOString().slice(0, 10);
    const [meta, setMeta] = useState({
        invoice_date: todayIso, po_no: "", transport: "", vehicle_no: "", lr_no: "",
        reverse_charge: false, terms: "", sales_ledger_id: "", customer_ledger_id: null,
    });
    const [billTo, setBillTo] = useState({ name: "", address: "", state_code: "", pin: "", gstin: "", phone: "" });
    const [shipTo, setShipTo] = useState({ name: "", address: "", state_code: "", pin: "", gstin: "", phone: "" });
    const [sameAsBill, setSameAsBill] = useState(true);
    const [lines, setLines] = useState([{ item_name: "", hsn: "", qty: 1, unit: "Nos", rate: 0, gst_rate: 18 }]);

    // ------- Load everything the form needs on open -------
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [s, st, lg, cp] = await Promise.all([
                    api.get("/invoice-series", { params: { kind: "sales" } }),
                    api.get("/invoicing/states"),
                    api.get("/ledgers"),
                    api.get("/company-profile"),
                ]);
                setSeries(s.data || []);
                const def = (s.data || []).find((x) => x.is_default) || (s.data || [])[0];
                setSeriesId(def?.id || "");
                setStates(st.data || []);
                const all = lg.data || [];
                setLedgers(all);
                setCustomers(all.filter((l) => l.party_type === "customer"));
                if (cp.data?.company_gstin?.length >= 2) {
                    setCompanyState(cp.data.company_gstin.slice(0, 2));
                } else if (cp.data?.state_name) {
                    // Fall back to matching the state name on file when no GSTIN is set.
                    const match = (st.data || []).find((s) => s.name.toLowerCase() === cp.data.state_name.toLowerCase());
                    if (match) setCompanyState(match.code);
                }
                // default terms
                if (cp.data?.default_terms) setMeta((m) => ({ ...m, terms: cp.data.default_terms }));
                // pre-select first "Sales - B2B" ledger if present
                const salesGuess = all.find((l) => /sales - b2b/i.test(l.group_name || "")) ||
                                    all.find((l) => /^sales/i.test(l.name || ""));
                if (salesGuess) setMeta((m) => ({ ...m, sales_ledger_id: salesGuess.id }));
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, [open]);

    // ------- Compute preview number for currently-selected series -------
    const currentSeries = useMemo(() => series.find((s) => s.id === seriesId), [series, seriesId]);
    const previewNo = useMemo(() => {
        if (!currentSeries) return "—";
        return `${currentSeries.prefix || ""}${currentSeries.next_number || 1}`;
    }, [currentSeries]);

    // ------- Place of supply + IGST vs CGST/SGST -------
    const posCode = useMemo(() => {
        if (billTo.gstin && billTo.gstin.length >= 2 && /^\d{2}/.test(billTo.gstin)) return billTo.gstin.slice(0, 2);
        return billTo.state_code || companyState || "";
    }, [billTo.gstin, billTo.state_code, companyState]);
    // If the company GSTIN / state code is missing, we cannot decide intra-vs-inter reliably.
    // Show it clearly instead of silently defaulting to CGST+SGST.
    const companyStateUnknown = !companyState;
    const isIgst = useMemo(
        () => Boolean(!companyStateUnknown && posCode && posCode !== companyState),
        [companyStateUnknown, companyState, posCode],
    );
    const posName = useMemo(() => {
        const s = states.find((x) => x.code === posCode);
        return s ? `${s.code}-${s.name}` : "";
    }, [posCode, states]);

    // ------- Totals (client-side, matches backend math exactly) -------
    const totals = useMemo(() => {
        let subtotal = 0, cgst = 0, sgst = 0, igst = 0;
        for (const ln of lines) {
            const t = (Number(ln.qty) || 0) * (Number(ln.rate) || 0);
            subtotal += t;
            const r = Number(ln.gst_rate) || 0;
            if (isIgst) {
                igst += t * r / 100;
            } else {
                cgst += t * (r / 2) / 100;
                sgst += t * (r / 2) / 100;
            }
        }
        return {
            subtotal: Math.round(subtotal * 100) / 100,
            cgst: Math.round(cgst * 100) / 100,
            sgst: Math.round(sgst * 100) / 100,
            igst: Math.round(igst * 100) / 100,
            total: Math.round((subtotal + cgst + sgst + igst) * 100) / 100,
        };
    }, [lines, isIgst]);

    // ------- Line editing helpers -------
    const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
    const addLine = () => setLines((ls) => [...ls, { item_name: "", hsn: "", qty: 1, unit: "Nos", rate: 0, gst_rate: 18 }]);
    const removeLine = (i) => setLines((ls) => ls.length <= 1 ? ls : ls.filter((_, idx) => idx !== i));

    // ------- Pick an existing customer -> auto-fill bill-to -------
    const pickCustomer = (c) => {
        setMeta((m) => ({ ...m, customer_ledger_id: c.id }));
        setBillTo((b) => ({
            ...b,
            name: c.name_parts?.invoice_name || c.name || "",
            gstin: c.gstin || "",
            phone: c.name_parts?.advance_phone || "",
        }));
        setCustomerSearch("");
    };

    const clearCustomerLink = () => setMeta((m) => ({ ...m, customer_ledger_id: null }));

    // ------- Submit -------
    const submit = async (postNow) => {
        if (!seriesId) return toast.error("Pick an invoice series");
        if (!meta.sales_ledger_id) return toast.error("Pick a Sales / Revenue ledger");
        if (!billTo.name.trim()) return toast.error("Bill-To customer name is required");
        if (!lines.length || !lines[0].item_name.trim()) return toast.error("Add at least one line item");
        setSaving(true);
        try {
            const payload = {
                series_id: seriesId,
                invoice_date: meta.invoice_date,
                place_of_supply_code: posCode || null,
                reverse_charge: meta.reverse_charge,
                po_no: meta.po_no || null,
                transport: meta.transport || null,
                vehicle_no: meta.vehicle_no || null,
                lr_no: meta.lr_no || null,
                bill_to: billTo,
                ship_to: sameAsBill ? null : shipTo,
                same_as_bill_to: sameAsBill,
                lines: lines.filter((l) => l.item_name.trim()).map((l) => ({
                    item_name: l.item_name.trim(),
                    hsn: l.hsn || null,
                    qty: Number(l.qty) || 0,
                    unit: l.unit || "Nos",
                    rate: Number(l.rate) || 0,
                    gst_rate: Number(l.gst_rate) || 0,
                })),
                sales_ledger_id: meta.sales_ledger_id,
                customer_ledger_id: meta.customer_ledger_id,
                terms: meta.terms || null,
                post_now: postNow,
            };
            const { data } = await api.post("/sales-invoices/manual", payload);
            toast.success(postNow
                ? `Invoice ${data.invoice_no} posted`
                : `Draft ${data.invoice_no} saved`);
            if (postNow && data.id) {
                // Open PDF in a new tab. Include token via cookie (already set) so
                // the auth interceptor works — no need to attach headers here.
                const url = `${api.defaults.baseURL}/sales-invoices/${data.id}/pdf`;
                window.open(url, "_blank", "noopener");
            }
            onClose();
            onDone?.(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const filteredCustomers = useMemo(() => {
        const q = customerSearch.trim().toLowerCase();
        if (!q) return [];
        return customers.filter((c) => (c.name || "").toLowerCase().includes(q)).slice(0, 6);
    }, [customers, customerSearch]);

    const salesLedgers = ledgers.filter((l) =>
        /sales|revenue|service|warranty|consult|agency|income|jobwork|commission/i.test(l.group_name || "") &&
        !/output cgst|output sgst|output igst/i.test(l.name || "")
    );

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="create-invoice-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText size={18} /> Create Sales Invoice
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">
                    {/* Top row — series + meta */}
                    <div className="grid gap-3 md:grid-cols-6">
                        <div className="md:col-span-2">
                            <Label>Invoice Series *</Label>
                            <select value={seriesId} onChange={(e) => setSeriesId(e.target.value)}
                                    className="w-full h-9 border rounded-sm px-2 text-sm"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid="ci-series">
                                {series.length === 0 ? <option value="">— No series set up —</option> :
                                    series.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}{s.is_default ? " (default)" : ""}</option>
                                    ))}
                            </select>
                            <div className="text-[11px] text-stone-500 mt-1 font-mono">
                                Next: <b className="text-purple-700">{previewNo}</b>
                            </div>
                        </div>
                        <div>
                            <Label>Invoice date *</Label>
                            <Input type="date" value={meta.invoice_date} onChange={(e) => setMeta({ ...meta, invoice_date: e.target.value })}
                                   className="h-9 text-sm" data-testid="ci-date" />
                        </div>
                        <div>
                            <Label>Place of supply</Label>
                            <select value={posCode || ""}
                                    onChange={(e) => setBillTo({ ...billTo, state_code: e.target.value })}
                                    className="w-full h-9 border rounded-sm px-2 text-sm"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid="ci-pos">
                                <option value="">— pick state —</option>
                                {states.map((s) => (
                                    <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                                ))}
                            </select>
                            <div className="text-[10px] text-stone-500 mt-1">
                                {companyStateUnknown ? (
                                    <span className="text-rose-700 font-medium">
                                        ⚠ Company state not set — please set Company GSTIN in Settings to auto-apply IGST vs CGST/SGST.
                                    </span>
                                ) : isIgst ? (
                                    <span className="text-purple-700 font-medium">Inter-state → IGST</span>
                                ) : (
                                    <span className="text-emerald-700 font-medium">Intra-state → CGST + SGST</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <Label>PO No.</Label>
                            <Input value={meta.po_no} onChange={(e) => setMeta({ ...meta, po_no: e.target.value })} className="h-9 text-sm" data-testid="ci-po" />
                        </div>
                        <div>
                            <Label>Transport</Label>
                            <Input value={meta.transport} onChange={(e) => setMeta({ ...meta, transport: e.target.value })} className="h-9 text-sm" data-testid="ci-transport" />
                        </div>
                        <div>
                            <Label>Vehicle No.</Label>
                            <Input value={meta.vehicle_no} onChange={(e) => setMeta({ ...meta, vehicle_no: e.target.value })} className="h-9 text-sm" data-testid="ci-vehicle" />
                        </div>
                        <div>
                            <Label>LR No.</Label>
                            <Input value={meta.lr_no} onChange={(e) => setMeta({ ...meta, lr_no: e.target.value })} className="h-9 text-sm" data-testid="ci-lr" />
                        </div>
                        <div className="md:col-span-4">
                            <Label>Sales / Revenue ledger *</Label>
                            <select value={meta.sales_ledger_id} onChange={(e) => setMeta({ ...meta, sales_ledger_id: e.target.value })}
                                    className="w-full h-9 border rounded-sm px-2 text-sm"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid="ci-sales-ledger">
                                <option value="">— pick a sales ledger —</option>
                                {salesLedgers.map((l) => (
                                    <option key={l.id} value={l.id}>{l.name} <>({l.group_name})</></option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Bill To / Ship To */}
                    <div className="grid gap-4 md:grid-cols-2">
                        <PartyBlock title="Bill To *" party={billTo} setParty={setBillTo} states={states}
                                    onCustomerSearch={setCustomerSearch} customerSearch={customerSearch}
                                    matches={filteredCustomers} onPickCustomer={pickCustomer}
                                    customerLinkedId={meta.customer_ledger_id} onClearLink={clearCustomerLink}
                                    testidPrefix="ci-bill" />
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label>Ship To</Label>
                                <label className="flex items-center gap-2 text-[11px] text-stone-600">
                                    <input type="checkbox" checked={sameAsBill}
                                           onChange={(e) => setSameAsBill(e.target.checked)}
                                           data-testid="ci-same-as-bill" />
                                    Same as Bill To
                                </label>
                            </div>
                            {sameAsBill ? (
                                <div className="border rounded-sm p-3 text-xs text-stone-500 italic bg-stone-50"
                                     style={{ borderColor: "var(--se-border)" }} data-testid="ci-ship-mirror">
                                    Ship-to will copy from Bill-to on save.
                                </div>
                            ) : (
                                <PartyBlock title="" party={shipTo} setParty={setShipTo} states={states}
                                            hideCustomerPicker
                                            testidPrefix="ci-ship" />
                            )}
                        </div>
                    </div>

                    {/* Line items */}
                    <div className="border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-4 py-2 border-b flex items-center justify-between"
                             style={{ borderColor: "var(--se-border)" }}>
                            <div className="text-sm font-medium">Line items</div>
                            <Button size="sm" variant="outline" onClick={addLine} data-testid="ci-add-line">
                                <Plus size={13} className="mr-1" /> Add row
                            </Button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-stone-500 border-b" style={{ borderColor: "var(--se-border)" }}>
                                        <th className="px-2 py-1 w-6">#</th>
                                        <th className="px-2 py-1">Item / Description</th>
                                        <th className="px-2 py-1 w-24">HSN/SAC</th>
                                        <th className="px-2 py-1 w-16 text-right">Qty</th>
                                        <th className="px-2 py-1 w-16">Unit</th>
                                        <th className="px-2 py-1 w-24 text-right">Rate</th>
                                        <th className="px-2 py-1 w-16 text-right">GST%</th>
                                        <th className="px-2 py-1 w-28 text-right">Amount</th>
                                        <th className="w-6"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((ln, i) => {
                                        const t = (Number(ln.qty) || 0) * (Number(ln.rate) || 0);
                                        const r = Number(ln.gst_rate) || 0;
                                        const lineTotal = t + (t * r / 100);
                                        return (
                                            <tr key={i} className="border-b" style={{ borderColor: "var(--se-border)" }}>
                                                <td className="px-2 py-1 text-stone-500">{i + 1}</td>
                                                <td className="px-2 py-1">
                                                    <Input value={ln.item_name} onChange={(e) => setLine(i, "item_name", e.target.value)}
                                                           className="h-8 text-xs" data-testid={`ci-line-${i}-name`} />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <Input value={ln.hsn} onChange={(e) => setLine(i, "hsn", e.target.value)}
                                                           className="h-8 text-xs font-mono" data-testid={`ci-line-${i}-hsn`} />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <Input type="number" step="0.01" value={ln.qty}
                                                           onChange={(e) => setLine(i, "qty", e.target.value)}
                                                           className="h-8 text-xs text-right" data-testid={`ci-line-${i}-qty`} />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <Input value={ln.unit} onChange={(e) => setLine(i, "unit", e.target.value)}
                                                           className="h-8 text-xs" data-testid={`ci-line-${i}-unit`} />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <Input type="number" step="0.01" value={ln.rate}
                                                           onChange={(e) => setLine(i, "rate", e.target.value)}
                                                           className="h-8 text-xs text-right" data-testid={`ci-line-${i}-rate`} />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <Input type="number" step="0.01" value={ln.gst_rate}
                                                           onChange={(e) => setLine(i, "gst_rate", e.target.value)}
                                                           className="h-8 text-xs text-right" data-testid={`ci-line-${i}-gst`} />
                                                </td>
                                                <td className="px-2 py-1 text-right font-mono">₹ {inr(lineTotal)}</td>
                                                <td className="px-1 py-1">
                                                    {lines.length > 1 ? (
                                                        <button onClick={() => removeLine(i)}
                                                                className="text-rose-500 hover:text-rose-700 p-1"
                                                                data-testid={`ci-line-${i}-remove`}>
                                                            <Trash size={12} />
                                                        </button>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Totals + Terms */}
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <Label>Terms &amp; conditions</Label>
                            <textarea
                                value={meta.terms} onChange={(e) => setMeta({ ...meta, terms: e.target.value })}
                                className="w-full border rounded-sm p-2 text-sm min-h-[100px]"
                                style={{ borderColor: "var(--se-border)" }}
                                placeholder="Falls back to the default in Settings if left blank."
                                data-testid="ci-terms"
                            />
                            <label className="flex items-center gap-2 text-xs mt-2">
                                <input type="checkbox" checked={meta.reverse_charge}
                                       onChange={(e) => setMeta({ ...meta, reverse_charge: e.target.checked })}
                                       data-testid="ci-rc" />
                                Reverse charge applies
                            </label>
                        </div>
                        <div className="border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                            <div className="text-xs text-stone-500 uppercase tracking-widest mb-2">Totals</div>
                            <div className="space-y-1 text-sm font-mono">
                                <Row label="Sub Total" value={totals.subtotal} />
                                {isIgst
                                    ? <Row label="IGST" value={totals.igst} />
                                    : (<>
                                        <Row label="CGST" value={totals.cgst} />
                                        <Row label="SGST" value={totals.sgst} />
                                    </>)}
                                <div className="border-t pt-1 mt-1 flex justify-between font-bold text-base"
                                     style={{ borderColor: "var(--se-border)" }}>
                                    <span>Total</span>
                                    <span>₹ {inr(totals.total)}</span>
                                </div>
                                {posName ? (
                                    <div className="text-[10px] text-stone-500 pt-1">Place of supply: <b>{posName}</b></div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-3 border-t" style={{ borderColor: "var(--se-border)" }}>
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button variant="outline" onClick={() => submit(false)} disabled={saving} data-testid="ci-save-draft">
                            {saving ? "Saving…" : "Save as Draft"}
                        </Button>
                        <Button onClick={() => submit(true)} disabled={saving}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="ci-post">
                            {saving ? "Posting…" : "Create & Post"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function Row({ label, value }) {
    return (
        <div className="flex justify-between">
            <span className="text-stone-600">{label}</span>
            <span>₹ {inr(value)}</span>
        </div>
    );
}


function PartyBlock({ title, party, setParty, states, testidPrefix,
                     onCustomerSearch, customerSearch, matches, onPickCustomer,
                     customerLinkedId, onClearLink, hideCustomerPicker }) {
    return (
        <div>
            {title ? <Label>{title}</Label> : null}
            <div className="border rounded-sm p-3 space-y-2" style={{ borderColor: "var(--se-border)" }}>
                {!hideCustomerPicker ? (
                    <div className="relative">
                        {customerLinkedId ? (
                            <div className="flex items-center justify-between text-[11px] px-2 py-1 rounded-sm bg-emerald-50 text-emerald-800 mb-1"
                                 data-testid={`${testidPrefix}-linked`}>
                                Linked to existing customer ledger.
                                <button onClick={onClearLink} className="text-rose-600 hover:underline">Unlink</button>
                            </div>
                        ) : null}
                        <Input placeholder="Search existing customer…" value={customerSearch || ""}
                               onChange={(e) => onCustomerSearch?.(e.target.value)}
                               className="h-8 text-xs mb-1"
                               data-testid={`${testidPrefix}-cust-search`} />
                        {matches?.length ? (
                            <div className="absolute z-10 w-full bg-white border rounded-sm shadow-md max-h-40 overflow-auto text-xs"
                                 style={{ borderColor: "var(--se-border)" }}>
                                {matches.map((c) => (
                                    <button key={c.id} onClick={() => onPickCustomer(c)}
                                            className="block w-full text-left px-2 py-1 hover:bg-stone-50"
                                            data-testid={`${testidPrefix}-cust-pick-${c.id}`}>
                                        {c.name} {c.gstin ? <span className="text-stone-400">· {c.gstin}</span> : null}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <Input placeholder="Name" value={party.name} onChange={(e) => setParty({ ...party, name: e.target.value })}
                       className="h-8 text-sm" data-testid={`${testidPrefix}-name`} />
                <textarea placeholder="Address" value={party.address}
                          onChange={(e) => setParty({ ...party, address: e.target.value })}
                          className="w-full border rounded-sm p-2 text-xs min-h-[54px]"
                          style={{ borderColor: "var(--se-border)" }}
                          data-testid={`${testidPrefix}-address`} />
                <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="GSTIN" value={party.gstin || ""}
                           onChange={(e) => setParty({ ...party, gstin: e.target.value.toUpperCase() })}
                           maxLength={15} className="h-8 text-xs font-mono"
                           data-testid={`${testidPrefix}-gstin`} />
                    <Input placeholder="Phone" value={party.phone || ""}
                           onChange={(e) => setParty({ ...party, phone: e.target.value })}
                           className="h-8 text-xs"
                           data-testid={`${testidPrefix}-phone`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <select value={party.state_code || ""}
                            onChange={(e) => setParty({ ...party, state_code: e.target.value })}
                            className="h-8 border rounded-sm px-2 text-xs"
                            style={{ borderColor: "var(--se-border)" }}
                            data-testid={`${testidPrefix}-state`}>
                        <option value="">— State —</option>
                        {states.map((s) => (
                            <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                        ))}
                    </select>
                    <Input placeholder="PIN" value={party.pin || ""} maxLength={6}
                           onChange={(e) => setParty({ ...party, pin: e.target.value })}
                           className="h-8 text-xs"
                           data-testid={`${testidPrefix}-pin`} />
                </div>
            </div>
        </div>
    );
}
